/**
 * Carousel Logos Block Script
 *
 * @package TotalDigital
 */

const setupColumn = (list, columnIndex, carouselId) => {
	const items = Array.from(list.querySelectorAll('.carousel-logos__item'));
	const itemCount = items.length;

	if (itemCount === 0) return;

	const visibleItems = itemCount / 2;
	const firstItem = items[0];
	if (!firstItem) return;

	const itemHeight = firstItem.offsetHeight;
	if (itemHeight === 0) return;

	items.forEach((item, index) => {
		item.style.transform = `translateY(${index * itemHeight}px)`;
	});

	const transitionTime = 1;
	const pauseTime = 2.5;
	const totalDuration = (transitionTime + pauseTime) * visibleItems;
	const transitionDurationPercent = (transitionTime / totalDuration) * 100;

	list.style.setProperty('--scroll-distance', `-${visibleItems * itemHeight}px`);
	list.style.setProperty('--scroll-duration', `${totalDuration}s`);
	list.style.setProperty('--scroll-delay', `${columnIndex * 0.2}s`);

	// Keyframe + style-element names are namespaced with the per-carousel
	// carouselId so multiple carousel-logos instances on the same page don't
	// overwrite each other's animations (each carousel can have a different
	// logo count → different itemHeight → different scroll distance).
	const keyframeName = `carousel-scroll-vertical-${carouselId}-${columnIndex}`;
	const styleId = `carousel-keyframes-${carouselId}-${columnIndex}`;

	let keyframes = `@keyframes ${keyframeName} {\n`;
	keyframes += '  0% { transform: translateY(0); }\n';

	let currentPercent = 0;

	for (let i = 1; i <= visibleItems; i++) {
		const yPosition = -i * itemHeight;
		const transitionEndPercent = currentPercent + transitionDurationPercent;
		const pauseEndPercent = (i / visibleItems) * 100;

		keyframes += `  ${transitionEndPercent.toFixed(2)}% { transform: translateY(${yPosition}px); }\n`;

		if (i < visibleItems) {
			keyframes += `  ${pauseEndPercent.toFixed(2)}% { transform: translateY(${yPosition}px); }\n`;
		} else {
			keyframes += `  100% { transform: translateY(${yPosition}px); }\n`;
		}

		currentPercent = pauseEndPercent;
	}

	keyframes += '}';

	const existingStyle = document.getElementById(styleId);
	if (existingStyle) existingStyle.remove();

	const style = document.createElement('style');
	style.id = styleId;
	style.textContent = keyframes;
	document.head.appendChild(style);

	list.style.animationName = keyframeName;
};

const waitForImages = (container) => {
	const images = Array.from(container.querySelectorAll('img'));
	const pending = images.filter(img => !img.complete);

	if (pending.length === 0) return Promise.resolve();

	return Promise.all(pending.map(img => new Promise(resolve => {
		img.addEventListener('load', resolve, { once: true });
		img.addEventListener('error', resolve, { once: true });
	})));
};

const initCarousel = (container) => {
	// Assign a unique ID to this carousel instance so its keyframes don't
	// collide with any other carousel-logos on the page. Stored on the element
	// itself (dataset) so subsequent re-init calls reuse the same namespace.
	if (!container.dataset.carouselId) {
		container.dataset.carouselId = `cl-${Math.random().toString(36).slice(2, 9)}`;
	}
	const carouselId = container.dataset.carouselId;

	const lists = container.querySelectorAll('.carousel-logos__list');
	const pauseBtn = container.querySelector('.carousel-logos__pause-btn');
	const pauseIcon = pauseBtn?.querySelector('.pause-icon');
	const playIcon = pauseBtn?.querySelector('.play-icon');
	let isPaused = false;
	let pauseTimeout = null;

	const transitionTime = 0.6;
	const pauseTime = 2.5;
	const singleStepDuration = (transitionTime + pauseTime) * 1000;

	const doPause = () => {
		container.classList.add('is-paused');
		if (pauseIcon) pauseIcon.style.display = 'none';
		if (playIcon) playIcon.style.display = 'inline-block';
		if (pauseBtn) pauseBtn.setAttribute('aria-label', 'Play carousel');
	};

	const doPlay = () => {
		container.classList.remove('is-paused');
		if (pauseIcon) pauseIcon.style.display = 'inline-block';
		if (playIcon) playIcon.style.display = 'none';
		if (pauseBtn) pauseBtn.setAttribute('aria-label', 'Pause carousel');
	};

	if (pauseBtn) {
		pauseBtn.addEventListener('click', () => {
			if (pauseTimeout) {
				clearTimeout(pauseTimeout);
				pauseTimeout = null;
			}

			if (isPaused) {
				isPaused = false;
				doPlay();
			} else {
				isPaused = true;
				const now = performance.now();
				const elapsed = now % singleStepDuration;
				const transitionDuration = transitionTime * 1000;

				if (elapsed < transitionDuration) {
					const waitTime = transitionDuration - elapsed;
					pauseTimeout = setTimeout(() => {
						doPause();
						pauseTimeout = null;
					}, waitTime);
				} else {
					doPause();
				}
			}
		});
	}

	waitForImages(container).then(() => {
		requestAnimationFrame(() => {
			lists.forEach((list, columnIndex) => setupColumn(list, columnIndex, carouselId));
		});
	});
};

document.addEventListener('DOMContentLoaded', () => {
	const carousels = document.querySelectorAll('.carousel-logos:not(.is-admin)');
	carousels.forEach(initCarousel);
});

document.addEventListener('swup:content:replace', () => {
	const oldKeyframes = document.querySelectorAll('[id^="carousel-keyframes-"]');
	oldKeyframes.forEach((style) => style.remove());

	const carousels = document.querySelectorAll('.carousel-logos:not(.is-admin)');
	carousels.forEach(initCarousel);
});

export default initCarousel;
